import { assertValidImageData, colorToHex } from './pixel-analyzer.js';
import { ANALYSIS_THRESHOLDS } from './thresholds.js';
import type {
  PixelColor,
  RgbaImageData,
  SolidBackgroundCategory,
  SolidBackgroundDetection,
} from './types.js';

export interface SolidBackgroundOptions {
  readonly quantizationStep?: number;
  readonly maximumSamples?: number;
  readonly minimumSamples?: number;
  readonly dominantRatio?: number;
  readonly opaqueRatio?: number;
  readonly maximumVariance?: number;
}

interface SampleBucket {
  count: number;
  red: number;
  green: number;
  blue: number;
  alpha: number;
  redSquared: number;
  greenSquared: number;
  blueSquared: number;
}

/** Conservatively inspects only the perimeter and small corner patches. */
export function detectSolidBackground(
  image: RgbaImageData,
  options: SolidBackgroundOptions = {},
): SolidBackgroundDetection {
  assertValidImageData(image);
  const config = resolveOptions(options);
  const buckets = new Map<string, SampleBucket>();
  const visited = new Set<number>();
  let sampleCount = 0;
  let opaqueCount = 0;

  const perimeter =
    image.width === 1 || image.height === 1
      ? image.width * image.height
      : image.width * 2 + (image.height - 2) * 2;
  const stride = Math.max(1, Math.ceil(perimeter / config.maximumSamples));

  const sample = (x: number, y: number): void => {
    const pixelIndex = y * image.width + x;
    if (visited.has(pixelIndex) || sampleCount >= config.maximumSamples) return;
    visited.add(pixelIndex);

    const offset = pixelIndex * 4;
    const r = image.data[offset] ?? 0;
    const g = image.data[offset + 1] ?? 0;
    const b = image.data[offset + 2] ?? 0;
    const a = image.data[offset + 3] ?? 0;
    const key = `${quantize(r, config.quantizationStep)},${quantize(
      g,
      config.quantizationStep,
    )},${quantize(b, config.quantizationStep)}`;
    const bucket = buckets.get(key);

    sampleCount += 1;
    if (a >= ANALYSIS_THRESHOLDS.alpha.nearOpaque) opaqueCount += 1;

    if (bucket) {
      bucket.count += 1;
      bucket.red += r;
      bucket.green += g;
      bucket.blue += b;
      bucket.alpha += a;
      bucket.redSquared += r * r;
      bucket.greenSquared += g * g;
      bucket.blueSquared += b * b;
    } else {
      buckets.set(key, {
        count: 1,
        red: r,
        green: g,
        blue: b,
        alpha: a,
        redSquared: r * r,
        greenSquared: g * g,
        blueSquared: b * b,
      });
    }
  };

  for (let x = 0; x < image.width; x += stride) {
    sample(x, 0);
    sample(x, image.height - 1);
  }
  sample(image.width - 1, 0);
  sample(image.width - 1, image.height - 1);

  for (let y = stride; y < image.height - 1; y += stride) {
    sample(0, y);
    sample(image.width - 1, y);
  }

  const radius = ANALYSIS_THRESHOLDS.solidBackground.cornerPatchRadius;
  const patchWidth = Math.min(image.width, radius + 1);
  const patchHeight = Math.min(image.height, radius + 1);
  for (let localY = 0; localY < patchHeight; localY += 1) {
    for (let localX = 0; localX < patchWidth; localX += 1) {
      sample(localX, localY);
      sample(image.width - localX - 1, localY);
      sample(localX, image.height - localY - 1);
      sample(image.width - localX - 1, image.height - localY - 1);
    }
  }

  let dominant: SampleBucket | null = null;
  for (const bucket of buckets.values()) {
    if (!dominant || bucket.count > dominant.count) dominant = bucket;
  }

  if (!dominant || sampleCount === 0) {
    return emptyResult(sampleCount);
  }

  const dominantColor: PixelColor = {
    r: Math.round(dominant.red / dominant.count),
    g: Math.round(dominant.green / dominant.count),
    b: Math.round(dominant.blue / dominant.count),
    a: Math.round(dominant.alpha / dominant.count),
  };
  const dominantRatio = dominant.count / sampleCount;
  const opaqueRatio = opaqueCount / sampleCount;
  const colorVariance = bucketVariance(dominant);
  // Tiny images do not contain enough independent boundary evidence for a
  // conservative background claim, even when every available pixel matches.
  const enoughSamples = sampleCount >= config.minimumSamples;
  const suspected =
    enoughSamples &&
    dominantRatio >= config.dominantRatio &&
    opaqueRatio >= config.opaqueRatio &&
    colorVariance <= config.maximumVariance;
  const category = categorizeColor(dominantColor);
  const confidence = confidenceScore(
    dominantRatio,
    opaqueRatio,
    colorVariance,
    config.maximumVariance,
    enoughSamples,
  );

  return {
    kind: 'solid-background',
    suspected,
    confidence,
    reasons: suspected
      ? solidBackgroundReasons(category, dominantColor, dominantRatio, opaqueRatio)
      : [],
    category: suspected ? category : 'none',
    dominantColor,
    dominantColorHex: colorToHex(dominantColor),
    dominantRatio,
    opaqueRatio,
    colorVariance,
    sampleCount,
  };
}

export const detectSuspectedSolidBackground = detectSolidBackground;

function resolveOptions(options: SolidBackgroundOptions): Required<SolidBackgroundOptions> {
  const defaults = ANALYSIS_THRESHOLDS.solidBackground;
  const resolved = {
    quantizationStep: options.quantizationStep ?? defaults.quantizationStep,
    maximumSamples: options.maximumSamples ?? defaults.maximumSamples,
    minimumSamples: options.minimumSamples ?? defaults.minimumSamples,
    dominantRatio: options.dominantRatio ?? defaults.dominantRatio,
    opaqueRatio: options.opaqueRatio ?? defaults.opaqueRatio,
    maximumVariance: options.maximumVariance ?? defaults.maximumVariance,
  };

  assertPositiveInteger(resolved.quantizationStep, 'quantizationStep');
  assertPositiveInteger(resolved.maximumSamples, 'maximumSamples');
  assertPositiveInteger(resolved.minimumSamples, 'minimumSamples');
  assertRatio(resolved.dominantRatio, 'dominantRatio');
  assertRatio(resolved.opaqueRatio, 'opaqueRatio');
  if (!Number.isFinite(resolved.maximumVariance) || resolved.maximumVariance < 0) {
    throw new RangeError('maximumVariance must be a non-negative finite number.');
  }
  return resolved;
}

function emptyResult(sampleCount: number): SolidBackgroundDetection {
  return {
    kind: 'solid-background',
    suspected: false,
    confidence: 0,
    reasons: [],
    category: 'none',
    dominantColor: null,
    dominantColorHex: null,
    dominantRatio: 0,
    opaqueRatio: 0,
    colorVariance: 0,
    sampleCount,
  };
}

function bucketVariance(bucket: SampleBucket): number {
  const count = bucket.count;
  const redMean = bucket.red / count;
  const greenMean = bucket.green / count;
  const blueMean = bucket.blue / count;
  const redVariance = bucket.redSquared / count - redMean * redMean;
  const greenVariance = bucket.greenSquared / count - greenMean * greenMean;
  const blueVariance = bucket.blueSquared / count - blueMean * blueMean;
  return Math.max(0, redVariance + greenVariance + blueVariance);
}

function confidenceScore(
  dominantRatio: number,
  opaqueRatio: number,
  variance: number,
  maximumVariance: number,
  enoughSamples: boolean,
): number {
  if (!enoughSamples) return 0;
  const varianceScore =
    maximumVariance === 0 ? Number(variance === 0) : 1 - variance / maximumVariance;
  return clampConfidence(
    (dominantRatio * 0.5 + opaqueRatio * 0.35 + clamp01(varianceScore) * 0.15) * 100,
  );
}

function categorizeColor(color: PixelColor): SolidBackgroundCategory {
  const { whiteLuminance, lightGrayLuminance, darkGrayLuminance, neutralChannelSpread } =
    ANALYSIS_THRESHOLDS.solidBackground;
  const maximum = Math.max(color.r, color.g, color.b);
  const minimum = Math.min(color.r, color.g, color.b);
  const luminance = 0.2126 * color.r + 0.7152 * color.g + 0.0722 * color.b;
  if (maximum - minimum > neutralChannelSpread) return 'other';
  if (luminance >= whiteLuminance) return 'white';
  if (luminance >= lightGrayLuminance) return 'light-gray';
  if (luminance <= 18) return 'black';
  if (luminance <= darkGrayLuminance) return 'dark-gray';
  return 'other';
}

function solidBackgroundReasons(
  category: SolidBackgroundCategory,
  color: PixelColor,
  dominantRatio: number,
  opaqueRatio: number,
): string[] {
  const name: Record<Exclude<SolidBackgroundCategory, 'none'>, string> = {
    white: 'white',
    'light-gray': 'light gray',
    'dark-gray': 'dark gray',
    black: 'black',
    other: 'solid',
  };
  const safeCategory = category === 'none' ? 'solid' : name[category];
  return [
    `A ${safeCategory} color (${colorToHex(color)}) covers ${Math.round(
      dominantRatio * 100,
    )}% of sampled edges and corners.`,
    `${Math.round(opaqueRatio * 100)}% of boundary samples are opaque or nearly opaque.`,
  ];
}

function quantize(channel: number, step: number): number {
  return Math.min(255, Math.round(channel / step) * step);
}

function assertPositiveInteger(value: number, name: string): void {
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive integer.`);
  }
}

function assertRatio(value: number, name: string): void {
  if (!Number.isFinite(value) || value < 0 || value > 1) {
    throw new RangeError(`${name} must be between 0 and 1.`);
  }
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function clampConfidence(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}
